import { useEffect, useState } from 'react';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { api } from '@/api';

// Owner (2026-10-11, as on the B-BBEE site): invitation only - no sign-up and no "forgot password"; the
// administrator sets passwords and MFA is mandatory (set up with an authenticator app at the first sign-in).

export function PasswordInput({ id, value, onChange, autoComplete, autoFocus, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="tca-password">
      <input
        id={id}
        className="tca-input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        placeholder={placeholder}
        spellCheck={false}
        required
      />
      <button type="button" className="tca-password-toggle" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

function CodeInput({ value, onChange }) {
  return (
    <input
      id="mfa-code"
      className="tca-input tca-code-input"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      inputMode="numeric"
      autoComplete="one-time-code"
      placeholder="000000"
      autoFocus
      required
    />
  );
}

function SubmitButton({ busy, children, disabled }) {
  return (
    <button type="submit" className="btn-shimmer-orange tca-auth-submit" disabled={busy || disabled}>
      {busy ? <span className="tca-inline"><Loader2 size={18} className="tca-spin" /> Please wait…</span> : <span>{children}</span>}
    </button>
  );
}

export default function Login({ onSignedIn }) {
  const [stage, setStage] = useState('credentials'); // credentials | mfa | mfa_setup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const goTo = (next) => {
    setError('');
    setCode('');
    setStage(next);
  };

  // Every sign-in call answers with the next stage, or 'done' plus the user
  const run = async (path, body) => {
    setBusy(true);
    setError('');
    try {
      const result = await api(path, { method: 'POST', body });
      if (result.stage === 'done') onSignedIn(result.user);
      else goTo(result.stage);
    } catch (err) {
      // The 10-minute sign-in window ran out: start again with the password
      if (err.status === 401 && path !== '/auth/login' && /timed out|sign in again/i.test(err.message)) {
        setPassword('');
        goTo('credentials');
      }
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (stage !== 'mfa_setup') return;
    setSetup(null);
    api('/auth/mfa-setup', { method: 'POST' })
      .then(setSetup)
      .catch((err) => {
        setError(err.message);
        if (err.status === 401) goTo('credentials');
      });
  }, [stage]);

  const submit = (e) => {
    e.preventDefault();
    if (stage === 'credentials') run('/auth/login', { email, password });
    else if (stage === 'mfa') run('/auth/mfa', { code });
    else if (stage === 'mfa_setup') run('/auth/mfa-setup/confirm', { code });
  };

  return (
    <div className="tca-auth">
      <form className="tca-auth-card" onSubmit={submit} noValidate data-testid={`login-${stage}`}>
        {stage === 'credentials' && (
          <>
            <div className="tca-auth-icon"><KeyRound size={26} /></div>
            <h2 className="tca-auth-title">Sign In</h2>
            <label className="tca-label" htmlFor="email">Email Address</label>
            <input
              id="email"
              className="tca-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
            <label className="tca-label" htmlFor="password">Password</label>
            <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="current-password" />
            {error && <div className="tca-auth-error" role="alert">{error}</div>}
            <SubmitButton busy={busy} disabled={!email || !password}>Sign In</SubmitButton>
            {/* Owner (2026-10-11): the administrator's email address to contact */}
            <p className="tca-auth-note">
              Access is by invitation only. Your username is your email address and your password was sent to you by
              the TC Auditors administrator. If you can't sign in, please contact{' '}
              <a href="mailto:admin@tcauditors.co.za" data-testid="link-admin-email">admin@tcauditors.co.za</a>.
            </p>
          </>
        )}

        {stage === 'mfa' && (
          <>
            <div className="tca-auth-icon"><Smartphone size={26} /></div>
            <h2 className="tca-auth-title">Two-Factor Authentication</h2>
            <p className="tca-auth-text">Enter the 6-digit code from your authenticator app.</p>
            <CodeInput value={code} onChange={setCode} />
            {error && <div className="tca-auth-error" role="alert">{error}</div>}
            <SubmitButton busy={busy} disabled={code.length !== 6}>Verify</SubmitButton>
            <p className="tca-auth-note">
              Lost your phone or changed apps? Please ask the TC Auditors administrator to reset your MFA:{' '}
              <a href="mailto:admin@tcauditors.co.za" data-testid="link-admin-email-mfa">admin@tcauditors.co.za</a>.
            </p>
          </>
        )}

        {stage === 'mfa_setup' && (
          <>
            <div className="tca-auth-icon"><ShieldCheck size={26} /></div>
            <h2 className="tca-auth-title">Set Up Two-Factor Authentication</h2>
            <p className="tca-auth-text">Two-factor authentication (MFA) is required to use this site.</p>
            <ol className="tca-auth-steps tca-numbered">
              <li>Open an authenticator app on your phone (Microsoft Authenticator, Google Authenticator or similar).</li>
              <li>Add an account and scan this QR code.</li>
              <li>Enter the 6-digit code the app shows.</li>
            </ol>
            {setup ? (
              <>
                <img className="tca-qr" src={setup.qr} alt="QR code for your authenticator app" />
                <p className="tca-auth-secret">
                  Can't scan it? Enter this key instead:<br />
                  <code>{setup.secret}</code>
                </p>
              </>
            ) : (
              <div className="tca-qr tca-qr-loading"><Loader2 size={28} className="tca-spin" /></div>
            )}
            <label className="tca-label" htmlFor="mfa-code">6-Digit Code</label>
            <CodeInput value={code} onChange={setCode} />
            {error && <div className="tca-auth-error" role="alert">{error}</div>}
            <SubmitButton busy={busy} disabled={code.length !== 6 || !setup}>Confirm</SubmitButton>
          </>
        )}
      </form>
    </div>
  );
}
